import {
  PLL_LINKS,
  CNBC_IMG,
  BROADCAST_IMG,
  REVERSE_BROADCAST_IMG,
  Dropdown,
  MAIN_PLL_URL,
} from '../../Config/League/league.config'
import type { EventGQL } from '../../Api/events/events.types'
import { createTicketUTM } from '../../Routing'
import { SEASON_SEGMENT_NAMES } from '../../Constants/season'
import { months, shortDays } from '../../Constants'
import { SeasonType } from '../../Context/SeasonContext/SeasonContext'

type EventWeekParams = {
  week: string
  events: EventGQL[]
}

export type WeekTicketData = {
  week: string
  tickets: string
  dayTickets: string[]
  suitesId: string
  waitlistUrl: string
  waitlist: boolean
}

export type ClockTime = {
  period: string
  clockTime: string
}

export const gameLeaderStats = ['points', 'faceoffsWon', 'groundBalls', 'saves']

const GamePeriodMap: { [key: string]: string } = {
  '1': '1st',
  '2': '2nd',
  '3': '3rd',
  '4': '4th',
}

/**
 *
 * Map events into specific weeks
 */
export const mapEventWeeks = ({ week, events }: EventWeekParams) => {
  const filteredEvents = events.filter((event) => event.week === week)
  return filteredEvents
}

/**
 *
 * Check if all events are champ series events
 */
export const csEventsOnly = (evs: EventGQL[]) => {
  const seasonEvs = evs.some((ev) => ev.seasonSegment === SEASON_SEGMENT_NAMES.regular)
  const csEvs = evs.some((ev) => ev.seasonSegment === SEASON_SEGMENT_NAMES.champseries)
  return csEvs && !seasonEvs
}

/**
 *
 * Map champ series events
 */
export const mapChampSeriesEvents = (
  events: EventGQL[],
  byType?: string
): { [key: string]: EventGQL[] } => {
  if (byType === 'day') {
    return mapEventsByDay(events)
  }
  // By segment
  return {
    roundRobin: events.filter(
      (event) => event?.description && event.description.toLowerCase().includes('round')
    ),
    semis: events.filter(
      (event) => event?.description && event.description.toLowerCase().includes('semi')
    ),
    championship: events.filter(
      (event) => event?.description && event.description.toLowerCase().includes('champ')
    ),
  }
}

/**
 *
 * Map events by date the game is using startTime
 */
export const mapEventsByDay = (events: EventGQL[]) => {
  const eventDays: { [key: string]: EventGQL[] } = events.reduce((acc, event) => {
    const { date } = timeStampToDate(event.startTime)
    const existingEventsInDay = acc[date] || []
    const isDuplicate = existingEventsInDay.some(
      (ev) => ev.startTime === event.startTime
    )

    return {
      ...acc,
      [date]: isDuplicate ? existingEventsInDay : [...existingEventsInDay, event],
    }
  }, {} as { [key: string]: EventGQL[] })

  return eventDays
}

/**
 *
 * Map events into specific weeks
 */
export const getWeekList = (events: EventGQL[]) => {
  let weekList: string[] = []
  events.map((e) => {
    let we = weekList.find((w) => w === e.week)
    if (!we) {
      //Week wasn't added yet
      weekList.push(e.week)
    }
    return
  })
  return weekList
}

/**
 *
 * Return list of dropdown weeks using events
 */
export const createWeekDropdown = (events: EventGQL[]) => {
  let weekList: Dropdown[] = [
    {
      label: 'All',
      value: 'all',
    },
  ]
  events.map((e) => {
    let we = weekList.find((w) => w.value === e.week)
    if (!we) {
      //Week wasn't added yet
      weekList.push({ label: `Week ${e.week}`, value: e.week })
    }
    return
  })
  return weekList
}

/**
 *
 * Return date range of all games in a specific weekend
 */
export const getWeekendRange = (events: EventGQL[]) => {
  if (events.length === 0) return
  // Only one event, grab the date
  if (events.length === 1) return getShortDate(events[0].startTime)
  const sorted = events.sort((a, b) => a.startTime - b.startTime)
  const [first] = sorted.slice(0)
  const [last] = sorted.slice(-1)
  const firstDay = getShortDate(first.startTime)
  const lastDay = getShortDate(last.startTime)
  return firstDay === lastDay ? firstDay : `${firstDay}${` - ${lastDay}`}`
}

/**
 *
 * Shifting events that already happened to the back
 */
export const setTickerStart = (events: EventGQL[]) => {
  const start = events.find((ev) => ev.eventStatus < 2)
  if (!start) {
    //last Item
    return events.length - 1
  }
  return start.gameNumber - 1
}

/**
 *
 * Return previewUrl for specific event
 */
export const getPreviewUrl = (slug: string, seasonSegment?: SeasonType) => {
  const isAllStarEvent =
    seasonSegment === SEASON_SEGMENT_NAMES.allstar || slug.includes('all_star')
  if (isAllStarEvent) {
    return PLL_LINKS.allstar
  } else {
    return `https://${MAIN_PLL_URL}/matchup/${slug}`
  }
}

/**
 *
 * Add utm to day ticks
 */
export const createDayTicketUrl = (event: EventGQL, ticketUrl: string) => {
  if (event.seasonSegment === 'champseries') {
    return PLL_LINKS.championshipSeriesTickets
  }
  if (!event.urlTicket) return PLL_LINKS.schedule
  return createTicketUTM({
    ticketId: ticketUrl,
  })
}

/**
 *
 * Get weekends that are special like Opening Weekend and Playoffs
 */
export const getSpecialWeek = (week: string) => {
  switch (week) {
    case '1':
      return 'Opening Weekend'
    case '6':
      return 'All Star Weekend'
    case '9':
      return 'Quarterfinals'
    case '10':
      return 'Semifinals'
    case '11':
      return 'Championship'
    default:
      return
  }
}


/**
 *
 * Check if event is a special week
 * Returns null if not a special week
 */
export const checkSpecialWeek = (events: EventGQL[]) => {
  if (events.length > 0) {
    if (events[0].seasonSegment === 'allstar') {
      return 'All-Star'
    }
    if (events[0].seasonSegment === 'post') {
      if (!events[0]?.description) return null
      if (events[0]?.description.toLowerCase().includes('quarterfinal')) {
        return 'Quarterfinals'
      }
      if (events[0]?.description.toLowerCase().includes('semifinal')) {
        return 'Semifinals'
      }
      if (events[0]?.description.toLowerCase().includes('championship')) {
        return 'Championship'
      }
      return events[0]?.description
    }
  }
  return ''
}

/**
 *
 * Get formatted game description for Championship Series events
 */
export const getChampSeriesGameTitle = (section?: string | null): string => {
  if (!section) return ''
  if (section === 'roundRobin') return 'Round Robin'
  if (section === 'semis') return 'Semifinals'
  if (section === 'championship') return 'Championship'
  return ''
}

const BROADCAST_IMG_MAP: Record<string, string> = {
  ESPN: `${BROADCAST_IMG}/ESPN_Main.png`,
  ESPN2: `${BROADCAST_IMG}/ESPN2_Main.png`,
  ABC: `${BROADCAST_IMG}/ABC_Main.png`,
  ESPNU: `${BROADCAST_IMG}/ESPNU_Black.png`,
  ESPN_APP: `https://premierlacrosseleague.com/wp-content/uploads/2026/02/espn_app_logo_color.webp`,
  'ESPN App': `https://premierlacrosseleague.com/wp-content/uploads/2026/02/espn_app_logo_color.webp`,
  Peacock: `${BROADCAST_IMG}/Peacock_Black.png`,
  NBC: `${BROADCAST_IMG}/NBC_Black.png`,
  CNBC: `${CNBC_IMG}/CNBC.png`,
}

const REVERSE_BROADCAST_IMG_MAP: Record<string, string> = {
  ESPN: `${REVERSE_BROADCAST_IMG}/ESPN_Main_White.png`,
  ESPN2: `${REVERSE_BROADCAST_IMG}/ESPN2_Main_White.png`,
  ABC: `${REVERSE_BROADCAST_IMG}/ABC_Main.png`,
  ESPNU: `${REVERSE_BROADCAST_IMG}/ESPNU_White.png`,
  ESPN_APP: `https://premierlacrosseleague.com/wp-content/uploads/2026/02/espn_app_logo_white.webp`,
  'ESPN App': `https://premierlacrosseleague.com/wp-content/uploads/2026/02/espn_app_logo_white.webp`,
  Peacock: `${REVERSE_BROADCAST_IMG}/Peacock_White.png`,
  NBC: `${REVERSE_BROADCAST_IMG}/NBC_White.png`,
  CNBC: `${CNBC_IMG}/CNBC.png`,
}

/**
 *
 * Return all broadcaster images for specific event
 */
export const getBroadcastImg = (broadcasters: string[]) => {
  const broadcastImgs = broadcasters.map((name) => {
    return name in BROADCAST_IMG_MAP ? BROADCAST_IMG_MAP[name] : ''
  })
  return broadcastImgs
}

/**
 *
 * Return all broadcaster images for specific event in reverse color for dark backgrounds
 */
export const getReverseBroadcastImg = (broadcasters: string[]) => {
  const broadcastImgs = broadcasters.map((name) =>
    name in REVERSE_BROADCAST_IMG_MAP ? REVERSE_BROADCAST_IMG_MAP[name] : ''
  )
  return broadcastImgs
}

/**
 *
 * Return team cal object that contains calendar Id
 */
export const getTeamCalId = (team: string) => {
  const obj = teamCalendarIds.find((t) => t.team === team)
  if (!obj) {
    return allAddEventsId
  }
  return obj.calId
}
/**
 *
 * Returning shortened date from timestamp
 */
export const getShortDate = (timeStamp: any) => {
  const s = new Date(timeStamp * 1000)
  const m = months[s.getMonth()]
  const date = s.getDate()
  const dateString = `${m || ''} ${date || ''}`
  return dateString
}

/**
 *
 * Returning long date from timestamp ie Thursday, May 1 7pm ET
 */
export const timeStampToDate = (timeStamp: any) => {
  const s = new Date(timeStamp * 1000)
  const dateStr = s.toLocaleTimeString('en-US')

  // get just the time
  const tm = dateStr.slice(0, -3)

  // remove extra :00 if exists
  let st = tm.includes(':00') ? tm.split(':00')[0] : tm

  // 3am is the time set for a game that doesn't have a time, return TBD
  if (st.includes(':23')) {
    st = 'TBA'
  }

  //check if am or pm
  const ampm = dateStr.includes('PM') ? 'pm' : 'am'

  const dateAndTime = {
    date: `${shortDays[s.getDay()]}, ${months[s.getMonth()]} ${s.getDate()}`,
    time: `${st}${ampm}`,
  }
  return dateAndTime
}

/**
 *
 * Returning game time in user's local time
 */
export const getGameTime = (timeStamp: any) => {
  const s = new Date(timeStamp * 1000)
  const dateStr = s.toLocaleTimeString('en-US')

  // get just the time
  const tm = dateStr.slice(0, -3)

  // remove extra :00 if exists
  const st = tm.includes(':00') ? tm.split(':00')[0] : tm

  // 3am is the time set for a game that doesn't have a time, return TBD
  if (st.includes(':23')) return 'TBA'

  //check if am or pm
  const ampm = dateStr.includes('PM') ? 'pm' : 'am'

  return `${st}${ampm}`
}

/**
 *
 * Return the period and clock time of a specific game
 */
export const getPeriodAndClockTime = (event: EventGQL) => {
  if (!event) return
  let period: string | number = ''
  if (event.period > 5) {
    const otNumber = event.period - 4 //If second overtime, event.period would be 6
    period = `OT${otNumber}`
  } else {
    period = event.period === 5 ? 'OT' : GamePeriodMap[event.period]
  }
  const clockSecs = formatClockSeconds(event?.clockSeconds)

  if (event?.period === 1 && event?.clockMinutes === 0 && event?.clockSeconds === 0) {
    return { period: 'End of 1st', clockTime: '' }
  }

  if (event?.period === 2 && event?.clockMinutes === 0 && event?.clockSeconds === 0) {
    return { period: 'Half', clockTime: '' }
  }

  if (event?.period === 3 && event?.clockMinutes === 0 && event?.clockSeconds === 0) {
    return { period: 'End of 3rd', clockTime: '' }
  }

  if (
    event?.period === 4 &&
    event?.clockMinutes === 0 &&
    event?.clockSeconds === 0 &&
    event?.visitorScore === event.homeScore
  ) {
    return { period: 'End of Reg', clockTime: '' }
  }

  let clockTime = ''
  clockTime = `${event?.clockMinutes || 0}:${clockSecs}`
  if (
    event?.clockMinutes !== 0 &&
    !event?.clockMinutes &&
    event?.clockSeconds !== 0 &&
    !event?.clockSeconds
  ) {
    clockTime = ``
  }
  return {
    period,
    clockTime,
  }
}

export const formatClockSeconds = (seconds?: number | null) => {
  if (seconds === undefined || seconds === null) return ''
  return seconds === 0 ? '00' : seconds < 10 ? `0${seconds}` : `${seconds}`
}

export const eventWeeks: string[] = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
]

export const allAddEventsId = 'BR334295'
export const teamCalendarIds = [
  {
    team: 'All Teams',
    calId: 'BR334295',
  },
  {
    team: 'Archers',
    calId: 'qU270710',
  },
  {
    team: 'Atlas',
    calId: 'py270705',
  },
  {
    team: 'Cannons',
    calId: 'hf340635',
  },
  {
    team: 'Chaos',
    calId: 'yN270706',
  },
  {
    team: 'Chrome',
    calId: 'fe270704',
  },
  {
    team: 'Redwoods',
    calId: 'Nc270565',
  },
  {
    team: 'Waterdogs',
    calId: 'Zg270709',
  },
  {
    team: 'Whipsnakes',
    calId: 'hp270707',
  },
]

export const showTicker = (url: string) => {
  if (
    url.includes('/games') ||
    url.includes('/career-leaders') ||
    url.includes('/advanced-stats')
  ) {
    return false
  }
  return true
}

/**
 *
 * Return if regseason has started or not
 */
export const hasRegularSeasonStarted = (events: EventGQL[]) => {
  if (events.length < 1) return false
  const regGames = events.filter((ev) => ev.seasonSegment === 'regular')
  if (regGames.length < 1) return false

  const sorted = regGames.sort((a, b) => a.startTime - b.startTime)
  const now = new Date()
  //Set current time to seconds
  const currentTime = now.getTime() / 1000
  const diff = sorted[0].startTime - currentTime
  if (diff < 0 || sorted[0].eventStatus > 0) {
    // First reg season game has started
    return true
  } else {
    return false
  }
}

/**
 *
 * Check if game is live
 */
export const isLiveGame = (ev: EventGQL) => {
  if (!ev?.startTime) return false
  const now = new Date()
  //Set current time to seconds
  const currentTime = now.getTime() / 1000
  //current time > start time = started
  const startStatus = ev.startTime - currentTime

  const hasEnded = ev.eventStatus >= 2

  if (startStatus < 1 && !hasEnded) return true
  return false
}

export const getEventMetaData = (event?: EventGQL | null) => {
  if (!event || !event?.homeTeam || !event?.awayTeam)
    return {
      title: 'Single Game Stats',
      description: 'Premier Lacrosse League game stats',
      keywords: 'PLL, game stats, scores, boxscore, gamecast, live stats',
    }

  const teamStrings = {
    home: `${event.homeTeam?.location ? ` ${event.homeTeam?.location}` : ''} ${
      event.homeTeam?.fullName
    }`,
    //away team location is at the beginning so no space needed
    away: `${event.awayTeam?.location ? `${event.awayTeam?.location}` : ''} ${
      event.awayTeam?.fullName
    }`,
  }
  const date = timeStampToDate(event.startTime)?.date
  const title = `${teamStrings.away} vs${teamStrings.home} Game ${date}`
  const description = `Premier Lacrosse League game stats for ${teamStrings.away} vs${teamStrings.home} game on ${date}`
  const keywords = `PLL, game stats, ${teamStrings.away},${teamStrings.home}, scores, boxscore, gamecast, live stats`

  return {
    title,
    description,
    keywords,
  }
}

/**
 *
 * Return the stats page URL for a game event
 */
export const getGameStatsUrl = (event: EventGQL): string => {
  return `/games/${event.year}/${event.slugname}`
}

/**
 *
 * Return the navigation URL for a game event based on its status
 * Returns stats URL for started/finished games, preview URL for scheduled games
 */
export const getGameNavigationUrl = (event: EventGQL): { url: string; isExternal: boolean } => {
  if (event.eventStatus === 0) {
    return { url: getPreviewUrl(event.slugname, event.seasonSegment), isExternal: true }
  }
  return { url: getGameStatsUrl(event), isExternal: false }
}

/**
 *
 * Filter and sort events for a specific week by start time
 */
export const getWeekGamesSorted = (week: string, events: EventGQL[], league?: string): EventGQL[] => {
  if(league === 'WLL') {
    events =events.filter((ev) => ev.league === 'WLL')
  }
  const filtered = mapEventWeeks({ week, events })
  return [...filtered].sort((a, b) => a.startTime - b.startTime)
}
